import { ElLoading, ElMessage } from "element-plus";
import { useOverlayStore } from "@/stores";
import { useAdditionalAttrStore } from "@/stores/AdditionalAttrStore";
import { useConfiguratorStore } from "@/stores/configuratorData";
import { configuratorIdStore } from "@/stores/configuratorIdStore";
import { configuratorOpenState } from "@/stores/configuratorOpenState";
import { useLoaderStore } from "@/stores/loaderState";
import { useLoginStore } from "@/stores/LoginStore";
import { favoriteStore } from "@/stores/favoriteStore";
import { RawTranslation } from "@/lang/lang.types";
import { reactive, UnwrapNestedRefs } from "vue";

/**
 * copy clipboard function
 */
export const copyClipboard = (value: string) => {
  const elInput = document.createElement("input");

  elInput.setAttribute("value", value);
  document.body.appendChild(elInput);
  elInput.select();

  try {
    if (document.execCommand("copy")) {
      const copyIcon = document.querySelectorAll<HTMLElement>(
        ".iconfont.icon-copy"
      );

      copyIcon.forEach((element) => {
        element = element as HTMLElement;
        element.classList.add("icon-status-success");
        element.style.color = "#00A36D";
      });

      setTimeout(() => {
        copyIcon.forEach((element) => {
          element = element as HTMLElement;
          element.classList.remove("icon-status-success");
          element.style.color = "";
        });
      }, 5000);
      return Promise.resolve();
    } else throw new Error();
  } catch (err) {
    ElMessage.error("Oops, this is a error message.");
    return Promise.reject(err);
  } finally {
    document.body.removeChild(elInput);
  }
};

export const openFullLoading = (autoClose: boolean, text?: string) => {
  const svg = `
  <circle class="path" cx="25" cy="25" r="20" fill="none"></circle>
  `;
  const loading = ElLoading.service({
    lock: true,
    text: text,
    svg: svg,
    customClass: "configurator-loading",
    background: "rgba(255, 255, 255, 0.9)",
  });
  if (autoClose) {
    setTimeout(() => {
      loading.close();
    }, 2000);
  }
  return loading;
};

export const openLoaderWhite = (autoClose: boolean, text?: string) => {
  const svg = `
  <circle class="path" cx="25" cy="25" r="20" fill="none"></circle>
  `;
  const loading = ElLoading.service({
    lock: true,
    text: text,
    svg: svg,
    customClass: "configurator-loading",
    background: "rgba(255, 255, 255, 1)",
  });
  if (autoClose) {
    setTimeout(() => {
      loading.close();
    }, 2000);
  }
  return loading;
};

export const resetAllStore = () => {
  useAdditionalAttrStore().$reset();
  useOverlayStore().$reset();
  useConfiguratorStore().$reset();
  // configuratorIdStore().$reset();
  configuratorOpenState().$reset();
  useLoaderStore().$reset();
  useLoginStore().$reset();
};

/**
 * only use click logo
 */
export const productReset = () => {
  useConfiguratorStore().$reset();
  useConfiguratorStore().setuserSelectedOptions([]);
  useAdditionalAttrStore().$reset();
};

/**
 * clear all store and logout
 */
export const storeReset = () => {
  useOverlayStore().closeOverlay();
  useOverlayStore().$reset();
  useLoginStore().$reset();
  favoriteStore().$reset();
  productReset();
  // configuratorIdStore().$reset();
  // window.localStorage.removeItem("state");
};

export function handleBeforeUnload(event: BeforeUnloadEvent) {
  event.preventDefault();
  event.returnValue = ""; // This is required for older browsers
}
export function handlePopstateUnload(event: PopStateEvent) {
  event.preventDefault();
  history.pushState(null, "", window.location.href);
}

export function handleRefresh(event: KeyboardEvent) {
  if (event.key === "F5" || (event.ctrlKey && event.key === "r")) {
    event.preventDefault();
    useOverlayStore().setConfirmClose(true, "f5");
  }
}

export const directLogout = () => {
  document.removeEventListener("keydown", handleRefresh);
  window.removeEventListener("beforeunload", handleBeforeUnload);
  window.removeEventListener("popstate", handlePopstateUnload);

  const loginStore = useLoginStore();
  loginStore.logout();
  storeReset();

  const handleEvent = new CustomEvent("logoutConnection", {
    bubbles: true,
    detail: true,
  });
  document.dispatchEvent(handleEvent);

  document.body.classList.remove("wika-dialog-open");
  document.documentElement.classList.remove("konfig-popup-hidden");
};

export const searchCode = async (
  value: string,
  closeLoading: boolean,
  callback: () => void,
  error: () => void
) => {
  const cis = configuratorIdStore();
  const attributeStore = useAdditionalAttrStore();
  const res = await cis.getConfiguratorByCode(value, closeLoading);

  if (res && res.data && typeof res.data !== "undefined") {
    attributeStore.setConfigWithCodeQuery(
      res.data.article_number,
      res.data.configurations
    );
    callback();
  } else {
    error();
  }
};

export const multiLang: UnwrapNestedRefs<Record<string, RawTranslation>> =
  reactive({
    "en-GB": {},
  });

export const cleanUrl = (url: string) => {
  const parsedUrl = new URL(url);
  parsedUrl.search = "";
  parsedUrl.hash = "";
  return parsedUrl.toString();
};

export const extractDataAndParams = (url: string) => {
  const parsedUrl = new URL(url);

  // Extract path segments
  const pathSegments = parsedUrl.pathname
    .split("/")
    .filter((segment) => segment !== "");

  // Extract query parameters
  const queryParams: { [key: string]: string[] } = {};
  for (const [key, value] of parsedUrl.searchParams.entries()) {
    if (!queryParams[key]) {
      queryParams[key] = [];
    }
    queryParams[key].push(value);
  }

  // Extract fragment
  const fragment = parsedUrl.hash.substring(1); // Remove the '#' symbol

  return { pathSegments, queryParams, fragment };
};
export const localeDateFormat = (date: string) => {
  const attribute = useAdditionalAttrStore();
  const lang = attribute.getlangValue;
  if (date === "0000-00-00T00:00:00.0") {
    return "1970-01-01T00:00:00.0";
  }
  return new Intl.DateTimeFormat(lang, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(date));
};

export function isWindowKey(key: string | number): key is keyof Window {
  for (const functionName in window) {
    if (typeof window[functionName] === "function") {
      if (key == functionName) {
        return true;
      }
    }
  }
  return false;
}

export function loadFont(
  fontName: string,
  fontFullName: string,
  fontUrlBase: string
) {
  const style = document.createElement("style");
  style.textContent = `
    @font-face {
      font-family: "${fontName}";
      src: url("${fontUrlBase}/fonts/${fontFullName}.woff2") format("woff2"),
          url("${fontUrlBase}/fonts/${fontFullName}.ttf") format("truetype");
    }
  `;
  document.head.appendChild(style);
}
